import { TFunction } from 'i18next';

import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { ServiceGivingHistoryItem } from '@sola/schema/dotcom/proxy-api/upstreamPayloads.js';
import { CommitmentCodeEnum } from '@sola/schema/dotcom/shared/commitments.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';

import {
  CommitmentCardOverrideData,
  getCommitmentTitle,
} from '../../molecules/commitment-card/utils';

export type GivingItemWithCommitment = ServiceGivingHistoryItem & {
  commitment?: GraphQLCommitment;
};

export type GroupedGivingHistoryItems = Record<
  string,
  GivingItemWithCommitment[]
>;

export enum MonthNamesEnum {
  January = 'January',
  February = 'February',
  March = 'March',
  April = 'April',
  May = 'May',
  June = 'June',
  July = 'July',
  August = 'August',
  September = 'September',
  October = 'October',
  November = 'November',
  December = 'December',
}

export enum MonthEnum {
  January,
  February,
  March,
  April,
  May,
  June,
  July,
  August,
  September,
  October,
  November,
  December,
}

const mapMonthNumToName = (num: MonthEnum): MonthNamesEnum | null => {
  switch (num) {
    case MonthEnum.January:
      return MonthNamesEnum.January;
    case MonthEnum.February:
      return MonthNamesEnum.February;
    case MonthEnum.March:
      return MonthNamesEnum.March;
    case MonthEnum.April:
      return MonthNamesEnum.April;
    case MonthEnum.May:
      return MonthNamesEnum.May;
    case MonthEnum.June:
      return MonthNamesEnum.June;
    case MonthEnum.July:
      return MonthNamesEnum.July;
    case MonthEnum.August:
      return MonthNamesEnum.August;
    case MonthEnum.September:
      return MonthNamesEnum.September;
    case MonthEnum.October:
      return MonthNamesEnum.October;
    case MonthEnum.November:
      return MonthNamesEnum.November;
    case MonthEnum.December:
      return MonthNamesEnum.December;
    default:
      return null;
  }
};

const mapNumToMonthNum = (num: number | string): MonthEnum => {
  switch (Number(num)) {
    case 0:
      return MonthEnum.January;
    case 1:
      return MonthEnum.February;
    case 2:
      return MonthEnum.March;
    case 3:
      return MonthEnum.April;
    case 4:
      return MonthEnum.May;
    case 5:
      return MonthEnum.June;
    case 6:
      return MonthEnum.July;
    case 7:
      return MonthEnum.August;
    case 8:
      return MonthEnum.September;
    case 9:
      return MonthEnum.October;
    case 10:
      return MonthEnum.November;
    case 11:
      return MonthEnum.December;
    default:
      return MonthEnum.January;
  }
};

export const getCommitmentDate = (
  givingHistoryItem: ServiceGivingHistoryItem,
) => {
  return givingHistoryItem?.recordDate || '';
};

/**
 * Returns an array of descending years of length `count`
 */
export const getYearList = (now: Date, count: number): number[] => {
  const yearList: number[] = [now.getFullYear()];

  while (yearList.length < count) {
    const lastYear = yearList[yearList.length - 1];
    yearList.push(lastYear - 1);
  }

  return yearList;
};

export const formatGivingItemsDateGroup = (
  givingItemsGroup: GroupedGivingHistoryItems,
) =>
  givingItemsGroup
    ? Object.entries(givingItemsGroup).map(([key, value]) => {
        const date = new Date(key);
        const options: Intl.DateTimeFormatOptions = {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        };
        const formattedDate = date.toLocaleDateString('en-US', options);

        return {
          heading: formattedDate,
          frequencyAbbr: '',
          givingHistoryItems: value,
        };
      })
    : [];

export const groupGivingItemsByDate = (
  givingHistoryItems: GivingItemWithCommitment[],
) => {
  const initialValue: GroupedGivingHistoryItems = {};

  return givingHistoryItems.reduce((acc, givingHistoryItem) => {
    if (givingHistoryItem.recordDate) {
      if (givingHistoryItem.recordDate in acc) {
        acc[givingHistoryItem.recordDate] = [
          ...acc[givingHistoryItem.recordDate],
          givingHistoryItem,
        ];
      } else {
        acc[givingHistoryItem.recordDate] = [givingHistoryItem];
      }
    }
    return acc;
  }, initialValue);
};

export const groupByDate = (givingHistoryItems: ServiceGivingHistoryItem[]) => {
  const initialValue: GroupedGivingHistoryItems = {};

  return givingHistoryItems.reduce((acc, givingHistoryItem) => {
    const dateString = givingHistoryItem.recordDate;
    if (dateString) {
      const date = new Date(dateString);
      const monthString = date.toLocaleDateString('en-US', {
        month: 'long',
        timeZone: 'UTC',
      });
      if (monthString in acc) {
        acc[monthString] = [...acc[monthString], givingHistoryItem];
      } else {
        acc[monthString] = [givingHistoryItem];
      }
    }
    return acc;
  }, initialValue);
};

export const groupGivingItemsByMonth = (
  givingHistoryItems: ServiceGivingHistoryItem[],
) => {
  const initialValue: Record<number, ServiceGivingHistoryItem[]> = {
    [MonthEnum.January]: [],
    [MonthEnum.February]: [],
    [MonthEnum.March]: [],
    [MonthEnum.April]: [],
    [MonthEnum.May]: [],
    [MonthEnum.June]: [],
    [MonthEnum.July]: [],
    [MonthEnum.August]: [],
    [MonthEnum.September]: [],
    [MonthEnum.October]: [],
    [MonthEnum.November]: [],
    [MonthEnum.December]: [],
  };

  return givingHistoryItems.reduce((acc, givingHistoryItem) => {
    const dateString = givingHistoryItem.recordDate;
    if (dateString) {
      const date = new Date(dateString);
      const monthNum = date.getUTCMonth();
      acc[monthNum] = [...acc[monthNum], givingHistoryItem];
    }
    return acc;
  }, initialValue);
};

export const orderCommitmentsByMonth = (
  a: ServiceGivingHistoryItem,
  b: ServiceGivingHistoryItem,
) => {
  if (!a.recordDate || !b.recordDate) {
    return 0;
  }
  const aDate = new Date(a.recordDate);
  const bDate = new Date(b.recordDate);
  return aDate.getUTCMonth() - bDate.getUTCMonth();
};

export const formatGivingItemsGroup = (
  year: number,
  group: Record<number, ServiceGivingHistoryItem[]>,
  t: TFunction<'my', undefined>,
) =>
  Object.entries(group).map(([monthNum, givingHistoryItems]) => ({
    heading: t('givingHistory.total', {
      month: mapMonthNumToName(mapNumToMonthNum(monthNum)),
      year,
    }),
    givingHistoryItems,
  }));

export const isSelectedYear =
  (year: number) =>
  (givingHistoryItem: ServiceGivingHistoryItem): boolean => {
    const commitmentYear =
      !!givingHistoryItem.recordDate &&
      Number(new Date(givingHistoryItem.recordDate).getFullYear());

    return commitmentYear ? commitmentYear === year : false;
  };

export const getGivingHistoryTotal = (
  givingHistoryItems: ServiceGivingHistoryItem[],
) =>
  givingHistoryItems.reduce((acc, givingHistoryItem) => {
    if (!givingHistoryItem?.amount) {
      return acc;
    }
    return givingHistoryItem.isRefund
      ? acc - givingHistoryItem.amount
      : acc + givingHistoryItem.amount;
  }, 0);

export const attachCommitmentToGivingItem =
  (commitments?: GraphQLCommitment[]) =>
  (givingItem: ServiceGivingHistoryItem): GivingItemWithCommitment =>
    commitments
      ? {
          ...givingItem,
          commitment: commitments.find(
            ({ id }) => id === givingItem.commitmentId,
          ),
        }
      : givingItem;

// TODO: move to a schema file:
export enum GivingTypeEnum {
  Stocks = 'Stocks',
}

export const formatOverrideData = (
  t: TFunction<'my', undefined>,
  givingItem: ServiceGivingHistoryItem,
): CommitmentCardOverrideData => {
  switch (givingItem.givingType) {
    case GivingTypeEnum.Stocks:
      return {
        title: t('commitments.givingTypes.stocks.title', {
          tickerSymbol: givingItem.stockTickerSymbol,
        }),
        label: t('commitments.givingTypes.stocks.label'),
        rate: t('commitments.givingTypes.stocks.rate', {
          count: givingItem.quantity ? +givingItem.quantity : 0,
        }),
      };
    default:
      return {
        title: givingItem.stockGiftInKindDescription,
        rate: `${givingItem.fundDescription}`,
      };
  }
};

/**
 * Translations to be used for gift commitments.
 */
const giftTranslations = {
  [CommitmentCodeEnum.BirthdayGift]: {
    titleKey: 'my:commitments.title.birthdayNoCommitment',
    labelKey: 'my:commitments.category.giftCommitment',
  },
  [CommitmentCodeEnum.GeneralOrChildGift]: {
    titleKey: 'my:commitments.title.childNoCommitment',
    labelKey: 'my:commitments.category.giftCommitment',
  },
  [CommitmentCodeEnum.FamilyGift]: {
    titleKey: 'my:commitments.title.familyNoCommitment',
    labelKey: 'my:commitments.category.giftCommitment',
  },
};

/**
 * Handle titles for refunds.
 */
const getTitleWithRefund = (
  isRefund: boolean,
  baseTitle: string,
  refundText: string,
) => (isRefund ? `${refundText} ${baseTitle}` : baseTitle);

/**
 * Format the data when there is no commitment object.
 */
export const formatNoCommitmentData = (
  t: TFunction<'my', 'common'>,
  givingItem: ServiceGivingHistoryItem,
): CommitmentCardOverrideData => {
  const refundText = t('my:commitments.refund');

  const translations =
    giftTranslations[givingItem.fundCode as keyof typeof giftTranslations];

  const baseTitle = translations
    ? t(translations.titleKey)
    : (givingItem.fundDescription ?? '');

  const label = translations
    ? t(translations.labelKey)
    : t('commitments.category.fundCommitment');

  const title = getTitleWithRefund(givingItem.isRefund, baseTitle, refundText);

  return {
    title,
    label,
  };
};

export const groupGivingHistoryByType = (
  givingHistory: GivingItemWithCommitment[],
) => {
  const initialValue: GroupedGivingHistoryItems = {
    [CommitmentTypeEnum.ChildSponsorship]: [],
    [CommitmentTypeEnum.Correspondence]: [],
    [CommitmentTypeEnum.FundCommitment]: [],
    [CommitmentTypeEnum.GiftCommitment]: [],
    [CommitmentTypeEnum.Intervention]: [],
    [CommitmentTypeEnum.LivingSponsorship]: [],
    [CommitmentTypeEnum.NA]: [],
  };
  return givingHistory.reduce((acc, givingItem) => {
    if (!givingItem.commitment?.commitmentType) {
      acc.NA = [...acc.NA, givingItem];
    } else if (
      givingItem.commitment?.commitmentType &&
      givingItem.commitment.commitmentType in acc
    ) {
      acc[givingItem.commitment.commitmentType] = [
        ...acc[givingItem.commitment.commitmentType],
        givingItem,
      ];
    }
    return acc;
  }, initialValue);
};

export const getSortCommitmentsByTitle =
  (t: TFunction<'my', undefined>) =>
  (a: GivingItemWithCommitment, b: GivingItemWithCommitment) => {
    if (!a.commitment && !b.commitment) {
      return 0;
    }

    if (!a.commitment) {
      return 1;
    }

    if (!b.commitment) {
      return -1;
    }

    return getCommitmentTitle(a.commitment, t)?.localeCompare(
      getCommitmentTitle(b.commitment, t),
    );
  };
